import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import { IconWrapper } from '../components/icons/IconWrapper';
import Button from '../components/ui/Button';
import { useNavigate } from 'react-router-dom';

const PageContainer = styled.div`
  padding: 2rem 0 5rem;
  background-color: ${({ theme }) => theme.colors.darkest};
  color: ${({ theme }) => theme.colors.light};
`;

const PageHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.light};
  padding: 4rem 0;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
`;

const PageDescription = styled.p`
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
`;

const ContactSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  h2 {
    font-size: 1.8rem;
    margin-bottom: 2rem;
    position: relative;
    padding-bottom: 0.75rem;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 80px;
      height: 3px;
      background: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.primary},
        ${({ theme }) => theme.colors.secondary}
      );
    }
  }
`;

const InfoItem = styled.div`
  display: flex;
  margin-bottom: 1.5rem;
`;

const InfoIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: ${({ theme }) => theme.borderRadius.round};
  background-color: ${({ theme }) => theme.colors.darker};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  h3 {
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.light};
  }
  
  p {
    color: ${({ theme }) => theme.colors.lightGray};
    margin-bottom: 0.25rem;
  }
  
  a {
    color: ${({ theme }) => theme.colors.lightGray};
    transition: ${({ theme }) => theme.transitions.short};
    
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const MapContainer = styled.div`
  margin-top: 2.5rem;
  height: 300px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  
  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ContactForm = styled.div`
  h2 {
    font-size: 1.8rem;
    margin-bottom: 2rem;
    position: relative;
    padding-bottom: 0.75rem;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 80px;
      height: 3px;
      background: linear-gradient(
        to right,
        ${({ theme }) => theme.colors.primary},
        ${({ theme }) => theme.colors.secondary}
      );
    }
  }
`;

const Form = styled.form`
  display: grid;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormLabel = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const FormInput = styled.input`
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1rem;
  transition: ${({ theme }) => theme.transitions.short};
  background-color: ${({ theme }) => theme.colors.darker};
  color: ${({ theme }) => theme.colors.light};
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
    box-shadow: 0 0 0 2px rgba(247, 141, 45, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: ${({ theme }) => theme.transitions.short};
  background-color: ${({ theme }) => theme.colors.darker};
  color: ${({ theme }) => theme.colors.light};
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
    box-shadow: 0 0 0 2px rgba(247, 141, 45, 0.2);
  }
`;

const FormSelect = styled.select`
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-family: ${({ theme }) => theme.fonts.primary};
  font-size: 1rem;
  background-color: ${({ theme }) => theme.colors.darker};
  color: ${({ theme }) => theme.colors.light};
  transition: ${({ theme }) => theme.transitions.short};
  
  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
    box-shadow: 0 0 0 2px rgba(247, 141, 45, 0.2);
  }
`;

const SubmitButton = styled.div`
  margin-top: 1rem;
`;

const SuccessMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.success};
  color: ${({ theme }) => theme.colors.light};
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: 1.5rem;
`;

const ErrorMessage = styled.div`
  background-color: ${({ theme }) => theme.colors.error || '#e74c3c'};
  color: ${({ theme }) => theme.colors.light};
  padding: 1rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  margin-bottom: 1.5rem;
`;

const LocationsSection = styled.section`
  background-color: ${({ theme }) => theme.colors.dark};
  padding: 4rem 0;
`;

const LocationsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const LocationsHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    max-width: 700px;
    margin: 0 auto;
    color: ${({ theme }) => theme.colors.darkGray};
  }
`;

const LocationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
`;

const LocationCard = styled.div`
  background-color: ${({ theme }) => theme.colors.darker};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const LocationImage = styled.div<{ image: string }>`
  height: 150px;
  background-image: url(${({ image }) => image});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #f8f8f8;
  border-bottom: 1px solid ${({ theme }) => theme.colors.darkGray};
`;

const LocationContent = styled.div`
  padding: 1.5rem;
  
  h3 {
    margin-bottom: 1rem;
    font-size: 1.25rem;
    color: ${({ theme }) => theme.colors.light};
  }
  
  address {
    font-style: normal;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.lightGray};
  }
  
  p {
    margin-bottom: 0.5rem;
    color: ${({ theme }) => theme.colors.lightGray};
    
    strong {
      font-weight: 600;
      color: ${({ theme }) => theme.colors.light};
    }
  }
`;

const ContactPage: React.FC = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general'
  });

  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pageTitle = 'Contact ElectroCable | Wire & Cable Solutions';
  const pageDescription = 'Get in touch with ElectroCable for expert advice, product inquiries, custom quotes, or support. Contact us via phone, email, or our online form. We are located in Tamarac, FL and serve clients globally.';
  const siteUrl = 'https://www.yourwebsite.com'; // Replace with your actual domain
  const contactPageUrl = `${siteUrl}/contact`;
  const ogImageUrl = `${siteUrl}/images/contact-us-og-image.jpg`; // Replace with a relevant OG image for the contact page
  const logoUrl = `${siteUrl}/images/logo.svg`; // Your main logo URL

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // If the user selects "Quote Request", redirect to the dedicated quote page
    if (name === 'inquiryType' && value === 'quote') {
      navigate('/request-quote');
      return;
    }

    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // Send form data to our API endpoint
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          inquiryType: formData.inquiryType,
          message: formData.message
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Network response was not ok');
      }

      // Reset form after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        inquiryType: 'general'
      });

      setSubmitted(true);

      // Hide success message after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    } catch (err) {
      setError('There was a problem sending your message. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={contactPageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:site_name" content="ElectroCable" />
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={contactPageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImageUrl} />

        {/* JSON-LD Structured Data for Organization & ContactPoint */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "ElectroCable",
            "url": siteUrl,
            "logo": logoUrl,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "", // Add street address if available and desired for schema
              "addressLocality": "Tamarac",
              "addressRegion": "FL",
              "postalCode": "33321",
              "addressCountry": "US"
            },
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "telephone": "+1-847-376-9187",
                "contactType": "customer support", // Or "sales", "technical support"
                "areaServed": "US", // Specify regions served, can be an array
                "availableLanguage": ["English", "Spanish"] // Add languages spoken
              },
              {
                "@type": "ContactPoint",
                "telephone": "+1-954-526-8042",
                "contactType": "customer support",
                "areaServed": "US",
                "availableLanguage": ["English", "Spanish"]
              },
              {
                "@type": "ContactPoint",
                "email": "info@electrocableus.com",
                "contactType": "customer support",
                "areaServed": "US",
                "availableLanguage": ["English", "Spanish"]
              },
              {
                "@type": "ContactPoint",
                "email": "sales@electrocableus.com",
                "contactType": "sales",
                "areaServed": "US",
                "availableLanguage": ["English", "Spanish"]
              }
            ]
          })}
        </script>
      </Helmet>

      <PageHeader>
        <PageTitle>Contact Us</PageTitle>
        <PageDescription>
          Have questions about our products or services? We're here to help.
          Get in touch with our team for support, information, or quotes.
        </PageDescription>
      </PageHeader>

      <ContactSection>
        <ContactInfo>
          <h2>Our Contact Information</h2>

          <InfoItem>
            <InfoIcon>
              <IconWrapper icon={FaMapMarkerAlt} size={20} />
            </InfoIcon>
            <InfoContent>
              <h3>Headquarters</h3>
              <p>10408 West Mcnab Road Tamarac, FL 33321</p>
              <p>United States</p>
            </InfoContent>
          </InfoItem>

          <InfoItem>
            <InfoIcon>
              <IconWrapper icon={FaPhone} size={20} />
            </InfoIcon>
            <InfoContent>
              <h3>Phone</h3>
              <p>
                <strong>Direct:</strong>{' '}
                <a href="tel:+18473769187">+1 847 376 9187</a>
              </p>
              <p>
                <strong>Office:</strong>{' '}
                <a href="tel:+19545268052">+1 954 526 8052</a>
              </p>
            </InfoContent>
          </InfoItem>

          <InfoItem>
            <InfoIcon>
              <IconWrapper icon={FaEnvelope} size={20} />
            </InfoIcon>
            <InfoContent>
              <h3>Email</h3>
              <p>
                <a href="mailto:info@electrocableus.com">info@electrocableus.com</a>
              </p>
              <p>
                <a href="mailto:sales@electrocableus.com">sales@electrocableus.com</a>
              </p>
            </InfoContent>
          </InfoItem>

          <InfoItem>
            <InfoIcon>
              <IconWrapper icon={FaClock} size={20} />
            </InfoIcon>
            <InfoContent>
              <h3>Business Hours</h3>
              <p>Monday - Friday: 8:00 AM - 5:00 PM CST</p>
              <p>Sunday: 10:00 AM - 5:00 PM CST</p>
              <p>Saturday: Closed</p>
            </InfoContent>
          </InfoItem>

          <MapContainer>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d114178.71339840403!2d-80.17918355!3d26.3683064!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d8e3a037395f47%3A0x953ab94297f1b85e!2sBoca%20Raton%2C%20FL%2033248!5e0!3m2!1sen!2sus!4v1702556231051!5m2!1sen!2sus"
              allowFullScreen
              loading="lazy"
              title="Elite Wire USA Headquarters"
            ></iframe>
          </MapContainer>
        </ContactInfo>

        <ContactForm>
          <h2>Send Us a Message</h2>

          {submitted && (
            <SuccessMessage>
              Thank you for your message! We'll get back to you as soon as possible.
            </SuccessMessage>
          )}

          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <FormLabel htmlFor="firstName">First Name*</FormLabel>
                <FormInput
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="lastName">Last Name*</FormLabel>
                <FormInput
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <FormLabel htmlFor="email">Email*</FormLabel>
                <FormInput
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </FormGroup>

              <FormGroup>
                <FormLabel htmlFor="phone">Phone</FormLabel>
                <FormInput
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <FormLabel htmlFor="inquiryType">Inquiry Type*</FormLabel>
              <FormSelect
                id="inquiryType"
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleChange}
                required
              >
                <option value="general">General Inquiry</option>
                <option value="product">Product Information</option>
                <option value="quote">Quote Request</option>
                <option value="support">Technical Support</option>
                <option value="other">Other</option>
              </FormSelect>
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="subject">Subject*</FormLabel>
              <FormInput
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="message">Message*</FormLabel>
              <FormTextarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <SubmitButton>
              <Button
                type="submit"
                variant="primary"
                size="large"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </SubmitButton>
          </Form>
        </ContactForm>
      </ContactSection>

      <LocationsSection>
        <LocationsContainer>
          <LocationsHeader>
            <h2>Our Global Locations</h2>
            <p>
              With manufacturing and supply facilities across the globe, we provide high-quality cable
              manufacturing and supply services on a regional and international scale, offering a range of
              manufactured-to-stock, future, and made-to-order items.
            </p>
          </LocationsHeader>

          <LocationsGrid>
            <LocationCard>
              <LocationImage image="/images/flags/us-flag.jpg" />
              <LocationContent>
                <h3>United States</h3>
                <address>
                  Boca 10408 West Mcnab Road Tamarac, FL 33321<br />
                  United States
                </address>
                <p><strong>Phone:</strong> +1 847 376 9187</p>
                <p><strong>Email:</strong> info@electrocableus.com</p>
              </LocationContent>
            </LocationCard>

            <LocationCard>
              <LocationImage image="/images/flags/ecuador-flag.jpg" />
              <LocationContent>
                <h3>Ecuador</h3>
                <address>
                  Quito, 170135<br />
                  Ecuador
                </address>
                <p><strong>Phone:</strong> Not available</p>
                <p><strong>Email:</strong> info@electrocableus.com</p>
              </LocationContent>
            </LocationCard>

            <LocationCard>
              <LocationImage image="/images/flags/australia-flag.jpg" />
              <LocationContent>
                <h3>Australia</h3>
                <address>
                  Sydney, NSW 2000<br />
                  Australia
                </address>
                <p><strong>Phone:</strong> Not available</p>
                <p><strong>Email:</strong> info@electrocableus.com</p>
              </LocationContent>
            </LocationCard>
          </LocationsGrid>
        </LocationsContainer>
      </LocationsSection>
    </PageContainer>
  );
};

export default ContactPage; 
